/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Film, Image as ImageIcon, Loader2, Check, X, Sparkles, ArrowRight, ShieldCheck, Music } from 'lucide-react';
import MentionAutocomplete from '@/components/MentionAutocomplete';
import axios from 'axios';
import { formatUploadError } from '@/lib/uploadError';

// Compact chip rendered under each uploaded file thumbnail. Calls the
// nomenclature engine on mount to flag wrongly-named files BEFORE the
// artist submits — saves a round-trip with a downstream reviewer who'd
// otherwise have to bounce it back for renaming.
function NomenclatureChip({ filename, idx }) {
  const [verdict, setVerdict] = React.useState(null);
  React.useEffect(() => {
    if (!filename) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    axios.post(`${base}/api/nomenclature/parse`, { filename })
      .then(r => setVerdict(r.data)).catch(() => setVerdict(null));
  }, [filename]);
  if (!verdict) return null;
  if (verdict.valid) {
    return (
      <div className="px-2 pb-1 text-[9px] text-emerald-400 font-mono truncate" data-testid={`nom-ok-${idx}`}>
        ✓ {verdict.task} v{String(verdict.version).padStart(3, '0')}
      </div>
    );
  }
  return (
    <div className="px-2 pb-1 text-[9px] text-rose-400 truncate" title={verdict.issues.join('; ')} data-testid={`nom-bad-${idx}`}>
      ⚠ rename to PROJECT_ENTITY_TASK_v###
    </div>
  );
}

const STAGES = [
  { code: 'vo',         label: 'Voice Over',     gate: 'client'   },
  { code: 'storyboard', label: 'Storyboard',     gate: 'client'   },
  { code: 'animatic',   label: 'Animatic',       gate: 'client'   },
  { code: 'layout',     label: 'Layout',         gate: 'client'   },
  { code: 'modeling',   label: 'Modeling',       gate: 'internal' },
  { code: 'rigging',    label: 'Rigging',        gate: 'internal' },
  { code: 'shading',    label: 'Shading',        gate: 'internal' },
  { code: 'anim',       label: 'Animation',      gate: 'internal' },
  { code: 'fx',         label: 'FX',             gate: 'internal' },
  { code: 'light',      label: 'Lighting',       gate: 'internal' },
  { code: 'comp',       label: 'Compositing',    gate: 'internal' },
  { code: 'render',     label: 'Final Render',   gate: 'internal' },
];

// Map the artist's assigned task_type → which Submit-for-Approval stages
// they're allowed to upload to. Keeps the dropdown short and focused so an
// animatics artist only sees the animatic + VO stages, not 12 unrelated ones.
const TASK_TYPE_TO_STAGES = {
  animatics:   ['animatic'],
  storyboard:  ['storyboard', 'animatic'],
  modeling:    ['modeling'],
  texturing:   ['shading'],
  rigging:     ['rigging'],
  layout:      ['layout'],
  animation:   ['anim'],
  fx:          ['fx'],
  lighting:    ['light'],
  comp:        ['comp'],
  render:      ['render'],
  sound:       ['vo'],
  concept_art: ['vo'],
  script:      [],
};

const PRESET_OPTIONS = [
  { v: 'web-720',        label: 'web-720 · lightest (~90% smaller)' },
  { v: 'web-1080',       label: 'web-1080 · full HD proxy' },
  { v: 'fast-1080p24',   label: 'fast-1080p24 · quick encode' },
  { v: 'cinema-proxy',   label: 'cinema-proxy · supervisor HQ' },
  { v: 'lossless-proxy', label: 'lossless-proxy · visually perfect' },
  { v: 'none',           label: 'none · original bytes' },
];

/** SubmitForApprovalPage
 * ======================
 * Full-page replacement for the old ReviewSubmitDialog modal. Artists,
 * freelancers, vendors, supervisors, creative directors all use this
 * to push shots/files into the 3-stage approval pipeline.
 */
export default function SubmitForApprovalPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [stage, setStage] = useState('anim');
  const [kind, setKind] = useState('shot');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [media, setMedia] = useState([]);
  const [preset, setPreset] = useState('web-720');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [skipInternal, setSkipInternal] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [myTaskTypes, setMyTaskTypes] = useState(null); // null = not loaded; [] = none

  const canSkipInternal = ['master_admin', 'production_manager'].includes(user?.role);
  const isPmOrAdmin = ['master_admin', 'production_manager', 'supervisor', 'creative_director'].includes(user?.role);

  // For artists (non-PM): pull their assigned tasks once and narrow the Stage
  // dropdown to ONLY the stages they're qualified for. Animatics-only artists
  // see the Animatic stage instead of the full 12-stage list.
  useEffect(() => {
    if (isPmOrAdmin) { setMyTaskTypes(null); return; }
    api.get('/tasks').then(r => {
      const types = Array.from(new Set((r.data || []).map(t => t.task_type).filter(Boolean)));
      setMyTaskTypes(types);
    }).catch(() => setMyTaskTypes([]));
  }, [isPmOrAdmin]);

  const allowedStages = (() => {
    if (isPmOrAdmin || myTaskTypes === null) return STAGES;
    const codes = new Set();
    for (const tt of myTaskTypes) (TASK_TYPE_TO_STAGES[tt] || []).forEach(c => codes.add(c));
    if (codes.size === 0) return STAGES; // fallback: show full list rather than empty dropdown
    return STAGES.filter(s => codes.has(s.code));
  })();

  // Auto-correct the active stage if it's no longer in the allowed list
  useEffect(() => {
    if (!allowedStages.find(s => s.code === stage)) {
      if (allowedStages.length) setStage(allowedStages[0].code);
    }
    // eslint-disable-next-line
  }, [allowedStages.length]);

  const stageObj = allowedStages.find(s => s.code === stage) || STAGES.find(s => s.code === stage) || STAGES[0];
  const VIDEO_ONLY_STAGES = new Set(['animatic', 'layout', 'anim', 'fx', 'comp', 'render', 'storyboard']);
  const isVideoOnlyStage = VIDEO_ONLY_STAGES.has(stage) && kind === 'shot';
  const isAudioStage = stage === 'vo' || kind === 'sound';
  const isImageKind = kind === 'image';
  // Asset kind = the artist is shipping a 3D working file (Blender .blend,
  // FBX, USD, etc.) for review. We accept the full Blender + interchange
  // family + texture passes + packed bundles. (Founder spec May 9, 2026 —
  // Khojendra needs to submit raw .blend files for Hansel modeling review.)
  const isAssetKind = kind === 'asset';
  const ASSET_ACCEPT = '.blend,.blend1,.fbx,.obj,.glb,.gltf,.usd,.usda,.usdc,.usdz,.abc,.ma,.mb,.exr,.hdr,.tga,.psd,.zip,.7z,.rar,.tar,.png,.jpg,.jpeg,.tiff,.tif,.webp';
  const acceptAttr = isImageKind
    ? 'image/*,.jpg,.jpeg,.png,.webp,.tiff,.exr,.psd'
    : isAudioStage
    ? 'audio/*,.mp3,.wav,.m4a,.ogg,.aac,.flac'
    : isVideoOnlyStage
    ? 'video/*,.mp4,.mov,.m4v,.mkv,.webm'
    : isAssetKind
    ? ASSET_ACCEPT
    : 'image/*,video/*,audio/*,.jpg,.jpeg,.png,.webp,.mp4,.mov,.m4v,.mkv,.webm,.mp3,.wav,.m4a,.ogg,.glb,.gltf,.fbx,.obj';

  // For ASSET kind submissions both a .blend (working file) AND a .glb / .gltf
  // (web-friendly preview) are mandatory. The .blend is the source of truth →
  // dual-saved to NAS for re-opening in Blender. The .glb gets auto-mounted in
  // ModelViewer3D so reviewers can spin/inspect the asset without downloading
  // or installing Blender. (Founder spec May 9, 2026.)
  const hasBlend = media.some(m => /\.(blend|blend1)$/i.test(m.filename || ''));
  const hasGLB   = media.some(m => /\.(glb|gltf)$/i.test(m.filename || ''));
  // Founder ask May 17, 2026: artists no longer need to export a .glb manually.
  // The VPS render service auto-generates a quad-preserved OBJ + a GLB from
  // the uploaded .blend (~3 s). So only the .blend is mandatory for an
  // Asset-kind submission. The .glb upload box stays as an optional override
  // for artists who *do* want to ship a custom-baked GLB.
  const assetMediaIncomplete = isAssetKind && !hasBlend;

  useEffect(() => {
    (async () => {
      try {
        // For vendors: use my-dashboard to get allotted projects (bypasses WO acceptance filter)
        if (!isPmOrAdmin) {
          try {
            const dashRes = await api.get('/vendor/my-dashboard');
            const myProjects = dashRes.data?.my_projects || [];
            if (myProjects.length > 0) {
              const list = myProjects.map(p => ({ id: p.project_id, name: p.project_name }));
              setProjects(list);
              if (list.length && !projectId) setProjectId(list[0].id);
              return;
            }
          } catch {}
        }
        // Fallback: fetch all projects (for PM/admin or if vendor dashboard fails)
        const { data } = await api.get('/projects');
        const list = data || [];
        setProjects(list);
        if (list.length && !projectId) setProjectId(list[0].id);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !projectId) {
      if (!projectId) setErr('Pick a project first');
      return;
    }
    // Stage-aware + Kind-aware validation
    if (isVideoOnlyStage) {
      const bad = files.filter(f => {
        const looksVideo = (f.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv)$/i.test(f.name);
        return !looksVideo;
      });
      if (bad.length) {
        setErr(`The "${stageObj.label}" stage accepts video only (MP4 / MOV / H.264). Rejected: ${bad.map(f => f.name).join(', ')}`);
        if (e.target) e.target.value = '';
        return;
      }
    }
    if (isAudioStage) {
      const bad = files.filter(f => {
        const looksAudio = (f.type || '').startsWith('audio/') || /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(f.name);
        return !looksAudio;
      });
      if (bad.length) {
        setErr(`Audio stage accepts audio only (MP3 / WAV). Rejected: ${bad.map(f => f.name).join(', ')}`);
        if (e.target) e.target.value = '';
        return;
      }
    }
    if (isImageKind) {
      const bad = files.filter(f => {
        const looksImage = (f.type || '').startsWith('image/') || /\.(jpg|jpeg|png|webp|tiff|exr|psd|bmp)$/i.test(f.name);
        return !looksImage;
      });
      if (bad.length) {
        setErr(`Image kind accepts images only (JPG, PNG, WEBP). Rejected: ${bad.map(f => f.name).join(', ')}`);
        if (e.target) e.target.value = '';
        return;
      }
    }
    if (isAssetKind) {
      // Asset kind = 3D working file. Accept the SCENE family + textures +
      // packed archives. Reject loose video/audio (those belong on shot
      // submissions). The reference image ASSET_ACCEPT list is the single
      // source of truth — keep this regex in sync.
      const bad = files.filter(f => !/\.(blend|blend1|fbx|obj|glb|gltf|usd|usda|usdc|usdz|abc|ma|mb|exr|hdr|tga|psd|zip|7z|rar|tar|png|jpg|jpeg|tiff|tif|webp)$/i.test(f.name));
      if (bad.length) {
        setErr(`Asset kind accepts 3D scene files (.blend, .fbx, .usd, .obj, .gltf, .abc), textures (.exr, .psd, .png), or packed bundles (.zip). Rejected: ${bad.map(f => f.name).join(', ')}`);
        if (e.target) e.target.value = '';
        return;
      }
    }
    setUploading(true); setErr('');
    let currentFile = null;
    const startedAt = Date.now();
    try {
      for (const file of files) {
        currentFile = file;
        const fd = new FormData();
        fd.append('project_id', projectId);
        fd.append('file', file);
        const isVideo = (file.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv)$/i.test(file.name);
        fd.append('preset', isVideo ? preset : 'none');
        // Tell backend which stage/kind so it can route the dual-save to the right NAS subfolder.
        fd.append('stage', stage || '');
        fd.append('kind', kind || '');
        // For Submit (non-tree) page, no specific shot/asset code is bound — backend will
        // drop into a generic stage folder. Tree page passes parent_code separately.
        const { data } = await api.post('/reviews/upload-media', fd);
        setMedia(prev => [...prev, data]);
      }
    } catch (e) {
      // Diagnostic error: surface HTTP status + file size + cause so the user
      // sees *why* it failed instead of a useless "Upload failed".
      setErr(formatUploadError(e, currentFile, startedAt));
    }
    setUploading(false);
  };

  const removeMedia = (idx) => setMedia(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!projectId)    { setErr('Pick a project'); return; }
    if (!media.length) { setErr('Attach at least one file'); return; }
    setSubmitting(true); setErr(''); setMsg('');
    try {
      await api.post('/reviews/submit', {
        project_id: projectId,
        kind, stage,
        title: title || `${stageObj.label} submission`,
        description,
        media,
        skip_internal: skipInternal && canSkipInternal,
      });
      setMsg('Submitted. Routing to supervisor / client now.');
      setTitle(''); setDescription(''); setMedia([]);
      setTimeout(() => navigate('/fl-tasks'), 900);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msgText = typeof d === 'string'
        ? d
        : (d?.error || d?.message || JSON.stringify(d) || 'Submit failed');
      setErr(msgText);
    }
    setSubmitting(false);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="submit-page">
      {/* Hero */}
      <div className="relative mb-8 overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-rose-500/5 to-transparent p-6">
        <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="absolute top-8 right-24 h-24 w-24 rounded-full bg-rose-500/10 blur-2xl" />
        <div className="relative flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Sparkles className="h-6 w-6 text-zinc-950"/>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white tracking-tight">Submit for Approval</h1>
            <p className="text-xs text-zinc-400 mt-1">
              Push your shot, asset, or file into the 3-stage review pipeline —
              <span className="text-amber-300"> Supervisor → Producer → Client</span>
              {stageObj.gate === 'client' && <> (direct-to-client stages skip the internal gate)</>}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-5 bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
        {/* Project */}
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Project</label>
          <select value={projectId} onChange={e=>setProjectId(e.target.value)} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-amber-400 outline-none" data-testid="project-select">
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {/* Stage + Kind */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Stage</label>
            <select value={stage} onChange={e=>setStage(e.target.value)} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-amber-400 outline-none" data-testid="stage-select">
              {allowedStages.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
            {!isPmOrAdmin && allowedStages.length < STAGES.length && (
              <p className="text-[9px] text-zinc-500 mt-1" data-testid="stage-filter-note">
                Showing the {allowedStages.length} stage{allowedStages.length === 1 ? '' : 's'} matching your assigned tasks.
              </p>
            )}
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Kind</label>
            <div className="mt-1 flex rounded border border-zinc-800 bg-zinc-900 p-0.5 flex-wrap">
              {['shot','asset','image','sound','bundle'].map(k => (
                <button key={k} type="button" onClick={()=>setKind(k)} data-testid={`kind-${k}`}
                  className={`flex-1 text-xs py-1.5 rounded transition-colors min-w-[60px] ${kind===k?'bg-amber-400 text-zinc-900 font-semibold':'text-zinc-400 hover:text-white'}`}>
                  {k.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {canSkipInternal && stageObj.gate === 'internal' && (
          <label className="flex items-start gap-2 text-[11px] text-zinc-400 bg-violet-500/5 border border-violet-500/30 rounded p-2 cursor-pointer select-none" data-testid="skip-internal-wrap">
            <input type="checkbox" checked={skipInternal} onChange={e=>setSkipInternal(e.target.checked)} className="mt-0.5" data-testid="skip-internal"/>
            <span><span className="font-semibold text-violet-200">Producer override</span> — skip Supervisor + Producer gates and send <strong>directly to the Client</strong>. Use only when you've personally vetted the material.</span>
          </label>
        )}

        {/* Title */}
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Title</label>
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder={`${stageObj.label} · Ch3 · Hensal`} className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-amber-400 outline-none" data-testid="submit-title" />
        </div>

        {/* Notes — with @mention support */}
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Notes for reviewer</label>
          <p className="text-[9px] text-zinc-600 mt-0.5 mb-1">Use @name to tag Supervisor, PM, or team members for notification</p>
          <MentionAutocomplete
            api={api}
            projectId={projectId}
            value={description}
            onChange={setDescription}
            dataTestid="submit-desc"
            placeholder="Anything specific to watch for? Use @name to notify team..."
          />
        </div>

        {/* Preset */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Media</label>
            <div className="flex items-center gap-1 text-[10px]">
              <span className="text-zinc-500 uppercase tracking-wider">Video proxy:</span>
              <select value={preset} onChange={e=>setPreset(e.target.value)} data-testid="preset-select"
                className="bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5 text-[10px] text-zinc-200 focus:border-amber-400 outline-none"
                title="HandBrake-style 24fps animation-tuned ffmpeg preset. Images never transcode.">
                {PRESET_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <label className="mt-1 flex items-center gap-2 border border-dashed border-zinc-700 hover:border-amber-400 rounded-lg px-4 py-8 text-zinc-400 hover:text-amber-400 cursor-pointer transition-colors" data-testid="media-dropzone">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin"/> : <Upload className="h-5 w-5" />}
            <span className="text-sm">
              {uploading
                ? 'Uploading & transcoding…'
                : isImageKind
                ? `Click to attach images (JPG, PNG, WEBP, TIFF, EXR, PSD)`
                : isAudioStage
                ? `Click to attach audio (MP3, WAV, M4A, OGG)`
                : isVideoOnlyStage
                ? `Click to attach ${stageObj.label} videos (MP4 / MOV / H.264)`
                : isAssetKind
                ? `Click to attach Blender / 3D scene files (.BLEND, FBX, USD, OBJ, GLTF, ABC, EXR, PSD, ZIP)`
                : 'Click to attach files (MP4, MOV, JPG, PNG, WAV, MP3)'}
            </span>
            <input type="file" accept={acceptAttr} multiple className="hidden" onChange={onPickFiles} data-testid="media-input" />
          </label>
          {isVideoOnlyStage && (
            <p className="mt-1.5 text-[10px] text-amber-300/80 flex items-center gap-1.5" data-testid="video-only-note">
              <Film className="w-3 h-3" />
              {stageObj.label} is a motion stage — only video files are accepted. The server will auto-transcode to a
              lighter <code className="text-amber-200 bg-amber-500/10 px-1 rounded">{preset}</code> proxy for snappy review playback. Images will be rejected.
            </p>
          )}
          {/* Founder ask May 17, 2026: ONLY a .blend is required. The VPS
              render service auto-generates the OBJ + GLB preview (~3 s).
              The optional .glb upload is kept as a fallback override for
              artists who want to ship a custom-baked GLB. */}
          {isAssetKind && (
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="asset-checklist">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[11px] ${hasBlend ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200' : 'bg-amber-500/10 border-amber-500/40 text-amber-200'}`}
                   data-testid={hasBlend ? 'checklist-blend-ok' : 'checklist-blend-missing'}>
                {hasBlend ? <Check className="w-3.5 h-3.5"/> : <Upload className="w-3.5 h-3.5"/>}
                <span><b>.BLEND</b> — working file <span className="text-[10px] opacity-70">(stored on NAS for re-opening)</span></span>
              </div>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[11px] ${hasGLB ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200' : 'bg-zinc-900 border-zinc-700 text-zinc-500'}`}
                   data-testid={hasGLB ? 'checklist-glb-ok' : 'checklist-glb-auto'}>
                {hasGLB ? <Check className="w-3.5 h-3.5"/> : <Sparkles className="w-3.5 h-3.5 text-indigo-300"/>}
                <span>
                  <b>.GLB / .GLTF</b> — {hasGLB ? '3D preview attached' : 'auto-generated from .blend'}{' '}
                  <span className="text-[10px] opacity-70">
                    {hasGLB ? '(reviewer spins it in-browser)' : '(VPS render service · ~3 s)'}
                  </span>
                </span>
              </div>
              {!hasBlend && (
                <p className="col-span-2 text-[10px] text-amber-300/90 mt-0.5">
                  Just drop your <code className="text-amber-200 bg-amber-500/10 px-1 rounded">.blend</code> file —
                  VOS will convert it to a quad-preserved 3D preview automatically. No need to export glTF anymore.
                </p>
              )}
            </div>
          )}

          {media.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {media.map((m, i) => (
                <div key={i} className="relative group bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                  <div className="aspect-video flex items-center justify-center">
                    {m.type === 'image' ? <ImageIcon className="h-6 w-6 text-zinc-500"/> : m.type === 'audio' ? <Music className="h-6 w-6 text-zinc-500"/> : <Film className="h-6 w-6 text-zinc-500"/>}
                  </div>
                  <div className="px-2 py-1.5 text-[10px] text-zinc-400 truncate border-t border-zinc-800">{m.filename}</div>
                  {/* V2 Nomenclature check chip — instantly shows artists if their
                      filename violates PROJECT_ENTITY_TASK_v### so they can rename
                      BEFORE submission (May 18 2026). */}
                  <NomenclatureChip filename={m.filename} idx={i} />
                  {m.transcode?.applied && (
                    <div className="px-2 pb-1 text-[9px] text-emerald-400 font-mono truncate" data-testid={`transcode-chip-${i}`}>
                      {m.transcode.output?.width}×{m.transcode.output?.height} · −{m.transcode.reduction_pct}%
                    </div>
                  )}
                  {m.nas?.status === 'ok' && (
                    <div className="px-2 pb-1 text-[9px] text-cyan-400 font-mono truncate" title={`NAS: ${m.nas.path}`} data-testid={`nas-chip-${i}`}>
                      🗄️ NAS · {m.nas.path?.split('/').slice(-3).join('/')}
                    </div>
                  )}
                  {m.nas?.status && m.nas.status !== 'ok' && m.nas.status !== 'skipped' && (
                    <div className="px-2 pb-1 text-[9px] text-zinc-500 font-mono truncate" title={m.nas.error || m.nas.status} data-testid={`nas-skipped-${i}`}>
                      ⚠ NAS · {m.nas.status === 'bridge_offline' ? 'bridge offline' : m.nas.status === 'no_mapping' ? 'no NAS mapping' : 'failed'}
                    </div>
                  )}
                  <button type="button" onClick={()=>removeMedia(i)}
                    title="Remove this file"
                    data-testid={`remove-media-${i}`}
                    className="absolute top-1 right-1 h-6 w-6 bg-rose-500 hover:bg-rose-400 rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 transition-colors">
                    <X className="h-3.5 w-3.5 text-white"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2" data-testid="submit-err">{err}</div>}
        {msg && <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2 flex items-center gap-2" data-testid="submit-msg"><ShieldCheck className="h-4 w-4"/>{msg}</div>}
      </div>

      {/* Beautiful Submit button */}
      <div className="mt-6 flex items-center justify-end gap-3">
        <button onClick={()=>navigate(-1)} className="px-5 py-3 text-xs text-zinc-400 hover:text-white" data-testid="submit-cancel">Cancel</button>
        <button
          onClick={submit}
          disabled={submitting || uploading || !media.length || !projectId || assetMediaIncomplete}
          data-testid="submit-confirm"
          title={assetMediaIncomplete ? 'Asset submissions require a .blend working file. The VPS render service will auto-generate the 3D preview.' : ''}
          className="group relative h-14 px-8 rounded-xl font-bold text-zinc-950 text-sm tracking-wide
                     bg-gradient-to-r from-amber-400 via-amber-300 to-rose-300
                     hover:from-amber-300 hover:via-amber-200 hover:to-rose-200
                     disabled:opacity-40 disabled:cursor-not-allowed disabled:saturate-50
                     shadow-[0_0_30px_rgba(251,191,36,0.35)] hover:shadow-[0_0_40px_rgba(251,191,36,0.55)]
                     transition-all flex items-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : <Check className="h-4 w-4"/>}
          {submitting ? 'Pushing to pipeline…' : 'Submit for Approval'}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1"/>
        </button>
      </div>
    </div>
  );
}
